/**
 * /checklist — recurring weekly + biweekly marketing operations.
 *
 * Items defined in src/lib/marketing-checklist.ts. Completion per
 * (template_id, period_start) lives in marketing.checklist_run.
 * When a new period starts, items flip back to pending automatically.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarCheck,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Circle,
  ExternalLink,
  History,
  MinusCircle,
  StickyNote,
  User,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { MotionCard } from '@/components/motion/MotionCard';
import {
  CATEGORY_LABELS,
  CHECKLIST,
  CADENCE_LABELS,
  OWNER_LABELS,
  type Cadence,
  type ChecklistItem,
  currentPeriodStart,
} from '@/lib/marketing-checklist';
import { marketing } from '@/lib/supabase/marketing';
import { cn } from '@/lib/utils';

type Status = 'pending' | 'done' | 'skipped';

interface RunRow {
  template_id: string;
  period_start: string;
  scope: string;
  status: Status;
  completed_at: string | null;
  completed_by: string | null;
  notes: string | null;
}

export function ChecklistPage() {
  const qc = useQueryClient();
  const [showHistory, setShowHistory] = useState(false);

  const runs = useQuery({
    queryKey: ['checklist-runs'],
    queryFn: async (): Promise<RunRow[]> => {
      const { data, error } = await marketing
        .from('checklist_run')
        .select('template_id, period_start, scope, status, completed_at, completed_by, notes')
        .order('period_start', { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as unknown as RunRow[];
    },
    staleTime: 30_000,
  });

  // Current-period status per template.
  const currentByTemplate = useMemo(() => {
    const map = new Map<string, RunRow>();
    for (const item of CHECKLIST) {
      const period = currentPeriodStart(item.cadence);
      const row = (runs.data ?? []).find(
        (r) => r.template_id === item.id && r.period_start === period && r.scope === '',
      );
      if (row) map.set(item.id, row);
    }
    return map;
  }, [runs.data]);

  const weekly = CHECKLIST.filter((c) => c.cadence === 'weekly');
  const biweekly = CHECKLIST.filter((c) => c.cadence === 'biweekly');

  const score = (items: ChecklistItem[]) => {
    let done = 0;
    let counted = 0;
    for (const it of items) {
      const r = currentByTemplate.get(it.id);
      const status = r?.status ?? 'pending';
      if (status === 'skipped') continue;
      counted += 1;
      if (status === 'done') done += 1;
    }
    return { done, total: counted, pct: counted > 0 ? (done / counted) * 100 : 0 };
  };

  const weeklyScore = score(weekly);
  const biweeklyScore = score(biweekly);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Marketing checklist</h1>
        <p className="text-sm text-muted-foreground">
          The operational rhythm. Weekly items reset every Monday; 15-day items every other
          Monday. Click to complete; expand for notes + deep links.
        </p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2">
        <ScoreCard
          title="This week"
          range={periodRangeLabel('weekly')}
          done={weeklyScore.done}
          total={weeklyScore.total}
          pct={weeklyScore.pct}
        />
        <ScoreCard
          title="This fortnight"
          range={periodRangeLabel('biweekly')}
          done={biweeklyScore.done}
          total={biweeklyScore.total}
          pct={biweeklyScore.pct}
        />
      </div>

      {runs.isLoading ? (
        <Card>
          <CardContent className="space-y-2 p-4">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </CardContent>
        </Card>
      ) : (
        <>
          <MotionCard delay={0.04}>
            <SectionCard
              title="Every week"
              items={weekly}
              currentByTemplate={currentByTemplate}
              onUpdate={() => qc.invalidateQueries({ queryKey: ['checklist-runs'] })}
            />
          </MotionCard>

          <MotionCard delay={0.07}>
            <SectionCard
              title="Every 15 days"
              items={biweekly}
              currentByTemplate={currentByTemplate}
              onUpdate={() => qc.invalidateQueries({ queryKey: ['checklist-runs'] })}
            />
          </MotionCard>

          <button
            type="button"
            onClick={() => setShowHistory((v) => !v)}
            className="inline-flex items-center gap-1.5 text-2xs uppercase tracking-wide text-muted-foreground hover:text-foreground"
          >
            <History className="h-3 w-3" />
            {showHistory ? 'Hide' : 'Show'} past periods
          </button>
          {showHistory && <PastPeriods runs={runs.data ?? []} />}
        </>
      )}
    </div>
  );
}

function ScoreCard({
  title,
  range,
  done,
  total,
  pct,
}: {
  title: string;
  range: string;
  done: number;
  total: number;
  pct: number;
}) {
  const color =
    pct >= 80
      ? 'text-emerald-600 dark:text-emerald-400'
      : pct >= 50
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-rose-600 dark:text-rose-400';
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-baseline justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
            <p className="mt-0.5 text-2xs text-muted-foreground">{range}</p>
          </div>
          <div className={cn('text-2xl font-semibold tabular-nums', color)}>
            {done} / {total}
          </div>
        </div>
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-300',
              pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-rose-500',
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function SectionCard({
  title,
  items,
  currentByTemplate,
  onUpdate,
}: {
  title: string;
  items: ChecklistItem[];
  currentByTemplate: Map<string, RunRow>;
  onUpdate: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarCheck className="h-4 w-4 text-violet-500" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {items.map((item) => (
          <ItemRow
            key={item.id}
            item={item}
            row={currentByTemplate.get(item.id)}
            onUpdate={onUpdate}
          />
        ))}
      </CardContent>
    </Card>
  );
}

const NEXT_STATUS: Record<Status, Status> = {
  pending: 'done',
  done: 'skipped',
  skipped: 'pending',
};

function ItemRow({
  item,
  row,
  onUpdate,
}: {
  item: ChecklistItem;
  row: RunRow | undefined;
  onUpdate: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(row?.notes ?? '');
  const status = row?.status ?? 'pending';

  const save = useMutation({
    mutationFn: async ({
      next,
      newNotes,
    }: {
      next?: Status;
      newNotes?: string;
    }) => {
      const period = currentPeriodStart(item.cadence);
      const nextStatus = next ?? status;
      const payload: Record<string, unknown> = {
        template_id: item.id,
        period_start: period,
        scope: '',
        status: nextStatus,
        notes: newNotes !== undefined ? newNotes : row?.notes ?? null,
      };
      if (nextStatus === 'done') {
        payload.completed_at = new Date().toISOString();
        payload.completed_by = null;
      } else if (nextStatus === 'pending') {
        payload.completed_at = null;
      }
      const { error } = await marketing
        .from('checklist_run')
        .upsert(payload, { onConflict: 'template_id,period_start,scope' });
      if (error) throw error;
    },
    onSuccess: onUpdate,
  });

  const cycle = () => save.mutate({ next: NEXT_STATUS[status] });

  return (
    <div
      className={cn(
        'rounded-md border border-border/40 bg-card transition-colors',
        status === 'done' && 'border-emerald-500/30 bg-emerald-500/5',
        status === 'skipped' && 'opacity-60',
      )}
    >
      <div className="flex items-start gap-2.5 p-3">
        <button
          type="button"
          onClick={cycle}
          disabled={save.isPending}
          className="mt-0.5 shrink-0"
          title={`Click: ${status} → ${NEXT_STATUS[status]}`}
        >
          <StatusIcon status={status} />
        </button>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="min-w-0 flex-1 text-left"
        >
          <div className="flex items-baseline justify-between gap-2">
            <span
              className={cn(
                'text-sm font-medium leading-tight',
                status === 'done' && 'line-through text-muted-foreground',
              )}
            >
              {item.title}
            </span>
            <Badge variant="muted" className="shrink-0 text-2xs">
              {CATEGORY_LABELS[item.category]}
            </Badge>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-2xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <User className="h-3 w-3" />
              {OWNER_LABELS[item.owner]}
            </span>
            {row?.completed_at && (
              <span>
                · done{' '}
                {new Date(row.completed_at).toLocaleString('en-IN', {
                  weekday: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            )}
            {row?.notes && (
              <span className="inline-flex items-center gap-1">
                <StickyNote className="h-3 w-3" />
                notes
              </span>
            )}
          </div>
        </button>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="shrink-0 text-muted-foreground hover:text-foreground"
        >
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
      </div>

      {expanded && (
        <div className="space-y-2 border-t border-border/40 px-3 py-2.5 text-xs">
          <p className="text-muted-foreground">{item.description}</p>
          <div className="flex flex-wrap gap-2">
            {item.to && (
              <Link
                to={item.to}
                className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-card px-2.5 py-1 text-2xs font-medium hover:bg-accent"
              >
                Open {item.to}
              </Link>
            )}
            {item.external && (
              <a
                href={item.external}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-card px-2.5 py-1 text-2xs font-medium hover:bg-accent"
              >
                External <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => {
              if (notes !== (row?.notes ?? '')) save.mutate({ newNotes: notes });
            }}
            placeholder="Notes — observations, blockers, outcome…"
            className="min-h-[60px] w-full rounded-md border border-border bg-background p-2 text-xs"
          />
        </div>
      )}
    </div>
  );
}

function StatusIcon({ status }: { status: Status }) {
  switch (status) {
    case 'done':
      return <CheckCircle2 className="h-5 w-5 text-emerald-500" />;
    case 'skipped':
      return <MinusCircle className="h-5 w-5 text-muted-foreground" />;
    default:
      return <Circle className="h-5 w-5 text-muted-foreground" />;
  }
}

function PastPeriods({ runs }: { runs: RunRow[] }) {
  const grouped = useMemo(() => {
    const m = new Map<string, RunRow[]>();
    for (const r of runs) {
      if (r.period_start === currentPeriodStart('weekly')) continue;
      if (r.period_start === currentPeriodStart('biweekly')) continue;
      const arr = m.get(r.period_start) ?? [];
      arr.push(r);
      m.set(r.period_start, arr);
    }
    return [...m.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [runs]);

  if (grouped.length === 0) {
    return (
      <p className="text-2xs text-muted-foreground">No history yet — complete items this week.</p>
    );
  }

  return (
    <div className="space-y-3">
      {grouped.slice(0, 8).map(([period, rows]) => {
        const done = rows.filter((r) => r.status === 'done').length;
        return (
          <Card key={period}>
            <CardHeader>
              <CardTitle className="text-sm font-medium">
                Week of {new Date(period).toLocaleDateString('en-IN', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
                <Badge variant="muted" className="ml-2 text-2xs">
                  {done} / {rows.length} done
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-xs">
              {rows.map((r) => {
                const item = CHECKLIST.find((c) => c.id === r.template_id);
                return (
                  <div key={`${r.template_id}-${r.scope}`} className="flex items-center gap-2">
                    <StatusIcon status={r.status} />
                    <span className={cn(r.status === 'done' && 'line-through text-muted-foreground')}>
                      {item?.title ?? r.template_id}
                    </span>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function periodRangeLabel(cadence: Cadence): string {
  const start = new Date(currentPeriodStart(cadence) + 'T00:00:00Z');
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + (cadence === 'weekly' ? 6 : 13));
  const fmt = (d: Date) =>
    d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
  return `${fmt(start)} → ${fmt(end)} · ${CADENCE_LABELS[cadence]}`;
}
